import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert, Box, Button, Chip, Dialog, DialogContent, DialogTitle, IconButton, Skeleton, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Typography
} from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import { CartesianGrid, Line, LineChart, ResponsiveContainer, Tooltip as ChartTooltip, XAxis, YAxis } from 'recharts';
import { apiError, fmtMoney } from '../../Documents/format';
import { BUCKET_COLORS, CHART, actionOf, bucketOf } from '../constants';
import { daysLeftLabel, fmtCompact, fmtDay, fmtQty, fmtStamp, signed } from '../format';
import { isCancelled, journalApi } from '../journalApi';
import { EntryHistory, JournalEntry, Movement } from '../types';
import { AXIS_TICK, BORDER, TipBox, TooltipInjected, tooltipPoint } from './analytics/chartKit';

interface Props {
  /** Запись из таблицы: шапка видна сразу, история догружается */
  entry: JournalEntry | null;
  onClose: () => void;
}

interface Point extends Movement { idx: number }

const HistoryTip: React.FC<TooltipInjected<Point>> = props => {
  const p = tooltipPoint(props);
  if (!p) return null;
  const a = actionOf(p.action);
  return (
    <TipBox
      title={a.label} subtitle={`${fmtStamp(p.date)} · ${p.actionBy || 'кто - не указано'}`}
      rows={[
        { label: 'Сумма', value: fmtMoney(p.totalSum, 2), color: CHART.sum, bold: true, hint: p.totalSumDelta ? signed(p.totalSumDelta, x => fmtMoney(x, 2)) : undefined },
        { label: 'Количество, уп.', value: fmtQty(p.quantity), hint: p.quantityDelta ? signed(p.quantityDelta, fmtQty) : undefined },
        { label: 'Цена', value: fmtMoney(p.price, 2) },
        { label: 'Срок годности', value: fmtDay(p.expiryDate) },
        { label: 'Серия', value: p.serialNumber || '-' }
      ]}
    />
  );
};

const Field: React.FC<{ label: string; value: React.ReactNode; mono?: boolean }> = ({ label, value, mono }) => (
  <Box sx={{ minWidth: 0 }}>
    <Typography sx={{ fontSize: '0.7rem', color: '#64748b' }}>{label}</Typography>
    <Typography sx={{ fontSize: '0.84rem', fontWeight: 600, color: '#0f172a', fontFamily: mono ? 'monospace' : undefined, wordBreak: 'break-word' }}>
      {value || '-'}
    </Typography>
  </Box>
);

/** Карточка записи журнала и вся история её изменений (кто, когда, что поменял) */
const EntryHistoryDialog: React.FC<Props> = ({ entry, onClose }) => {
  const [data, setData] = useState<EntryHistory | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [retry, setRetry] = useState(0);
  const id = entry?.id ?? null;

  useEffect(() => {
    if (id === null) return undefined;
    const ctrl = new AbortController();
    setData(null);
    setError(null);
    journalApi.history(id, ctrl.signal)
      .then(res => setData(res))
      .catch(err => { if (!isCancelled(err)) setError(apiError(err, 'Не удалось загрузить историю записи')); });
    return () => ctrl.abort();
  }, [id, retry]);

  // Шапка: свежие данные с сервера, пока их нет - строка из таблицы
  const e = data?.entry || entry;
  const moves = useMemo<Point[]>(() => (data?.movements || []).map((m, idx) => ({ ...m, idx })), [data]);
  const rows = useMemo(() => moves.slice().reverse(), [moves]);

  if (!e) return null;
  const bucket = BUCKET_COLORS[bucketOf(e.daysLeft)];

  return (
    <Dialog
      open={Boolean(entry)} onClose={onClose} maxWidth={false}
      PaperProps={{ sx: { width: '100%', maxWidth: 980, borderRadius: 3, boxShadow: '0 20px 48px rgba(15, 23, 42, 0.16)', overflow: 'hidden' } }}
    >
      <DialogTitle sx={{ py: 1.5, px: 2.5, display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2, borderBottom: `1px solid ${BORDER}` }}>
        <Box sx={{ minWidth: 0 }}>
          <Typography sx={{ fontSize: '0.74rem', color: '#64748b' }}>
            Запись #{e.id} · Аптека №{e.pharmacyNum}{e.cityId ? ` · ${e.cityId}` : ''}
          </Typography>
          <Typography sx={{ fontSize: '1rem', fontWeight: 700, color: '#0f172a', lineHeight: 1.35 }}>{e.materialName || '-'}</Typography>
          <Box sx={{ display: 'flex', gap: 0.75, mt: 0.75, flexWrap: 'wrap' }}>
            <Chip size="small" label={`Годен до ${fmtDay(e.expiryDate)}`} sx={{ height: 22, fontSize: 11.5, fontWeight: 600, color: bucket.color, bgcolor: bucket.bg }} />
            <Chip size="small" label={daysLeftLabel(e.daysLeft)} sx={{ height: 22, fontSize: 11.5, fontWeight: 600, color: bucket.color, bgcolor: bucket.bg }} />
            {e.serialNumber && <Chip size="small" label={`Серия ${e.serialNumber}`} variant="outlined" sx={{ height: 22, fontSize: 11.5, borderColor: BORDER }} />}
          </Box>
        </Box>
        <IconButton onClick={onClose} size="small" aria-label="Закрыть" sx={{ color: 'text.secondary', p: 0.5 }}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 2.5, display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Box sx={{ mt: 2, display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', gap: 1.5, p: 1.5, borderRadius: 2, bgcolor: '#f8fafc', border: `1px solid #eef2f7` }}>
          <Field label="Сумма" value={fmtMoney(e.totalSum, 2)} />
          <Field label="Количество" value={`${fmtQty(e.quantity)} уп. / ${fmtQty(e.quantityPcs)} шт.`} />
          <Field label="Цена" value={fmtMoney(e.price, 2)} />
          <Field label="Код" value={e.nomenclatureCode} mono />
          <Field label="ШК" value={e.barcode} mono />
          <Field label="Производитель" value={e.producer} />
          <Field label="Категория" value={e.categoryName} />
          <Field label="Менеджер" value={e.managerName} />
          <Field label="Ценовая группа" value={e.priceGroup} />
          <Field label="RX/OTC" value={e.rxotc} />
          <Field label="Внесено" value={`${fmtStamp(e.recordDate)}${e.createdBy ? ` · ${e.createdBy}` : ''}`} />
          <Field label="Изменено" value={fmtStamp(e.updateDate)} />
          <Field label="Срок на дату записи" value={daysLeftLabel(e.daysAtRecord)} />
        </Box>
        {e.comment && (
          <Alert severity={/серия не совпадает/i.test(e.comment) ? 'warning' : 'info'} icon={false} sx={{ py: 0.25 }}>
            <b>Комментарий аптеки:</b> {e.comment}
          </Alert>
        )}

        {error && (
          <Alert severity="error" action={<Button color="inherit" size="small" onClick={() => setRetry(r => r + 1)}>Повторить</Button>}>{error}</Alert>
        )}

        {!data && !error && <Skeleton variant="rounded" height={220} sx={{ borderRadius: 2 }} />}

        {data && moves.length >= 2 && (
          <Box>
            <Typography sx={{ fontSize: '0.86rem', fontWeight: 700, mb: 0.5 }}>Сумма записи по изменениям</Typography>
            <Box sx={{ height: 200 }}>
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={moves} margin={{ top: 8, right: 16, left: 4, bottom: 0 }}>
                  <CartesianGrid stroke={CHART.grid} vertical={false} />
                  <XAxis dataKey="idx" tick={AXIS_TICK} tickFormatter={i => fmtStamp(moves[Number(i)]?.date).slice(0, 8)} axisLine={{ stroke: BORDER }} tickLine={false} />
                  <YAxis tick={AXIS_TICK} tickFormatter={v => fmtCompact(Number(v))} axisLine={false} tickLine={false} width={64} />
                  <ChartTooltip content={<HistoryTip />} />
                  <Line
                    type="stepAfter" dataKey="totalSum" stroke={CHART.sum} strokeWidth={2} isAnimationActive={false}
                    dot={(dp: { cx?: number; cy?: number; payload?: Point; index?: number }) => (
                      <circle key={dp.index} cx={dp.cx} cy={dp.cy} r={4} fill="#fff" stroke={actionOf(dp.payload?.action || '').color} strokeWidth={2} />
                    )}
                  />
                </LineChart>
              </ResponsiveContainer>
            </Box>
          </Box>
        )}

        {data && (
          <Box>
            <Typography sx={{ fontSize: '0.86rem', fontWeight: 700, mb: 0.75 }}>
              История изменений{moves.length ? ` (${moves.length})` : ''}
            </Typography>
            {!moves.length ? (
              <Typography sx={{ fontSize: '0.82rem', color: 'text.secondary' }}>По записи нет истории изменений</Typography>
            ) : (
              <TableContainer sx={{ border: `1px solid ${BORDER}`, borderRadius: 2, maxHeight: 360 }}>
                <Table size="small" stickyHeader sx={{ minWidth: 860 }}>
                  <TableHead>
                    <TableRow>
                      {['Дата', 'Действие', 'Кто', 'Кол-во, уп.', 'Сумма', 'Цена', 'Срок', 'Серия', 'Комментарий'].map(h => (
                        <TableCell key={h} sx={{ fontWeight: 700, fontSize: 12, color: '#475569', bgcolor: '#f8fafc', whiteSpace: 'nowrap' }}>{h}</TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {rows.map(m => {
                      const a = actionOf(m.action);
                      return (
                        <TableRow key={m.id} hover sx={{ '& td': { fontSize: 12.5, py: 0.75, borderBottom: '1px solid #f1f5f9' } }}>
                          <TableCell sx={{ whiteSpace: 'nowrap' }}>{fmtStamp(m.date)}</TableCell>
                          <TableCell>
                            <Chip size="small" label={a.label} sx={{ height: 22, fontSize: 11.5, fontWeight: 600, color: a.color, bgcolor: a.bg }} />
                          </TableCell>
                          <TableCell sx={{ color: '#475569' }}>{m.actionBy || '-'}</TableCell>
                          <TableCell sx={{ whiteSpace: 'nowrap' }}>
                            {fmtQty(m.quantity)}
                            {m.quantityDelta ? <Box component="span" sx={{ ml: 0.75, fontSize: 11.5, color: m.quantityDelta > 0 ? '#15803d' : '#b91c1c' }}>{signed(m.quantityDelta, fmtQty)}</Box> : null}
                          </TableCell>
                          <TableCell sx={{ whiteSpace: 'nowrap', fontWeight: 600 }}>
                            {fmtMoney(m.totalSum, 2)}
                            {m.totalSumDelta ? (
                              <Box component="span" sx={{ display: 'block', fontSize: 11.5, fontWeight: 400, color: m.totalSumDelta > 0 ? '#15803d' : '#b91c1c' }}>
                                {signed(m.totalSumDelta, x => fmtMoney(x, 2))}
                              </Box>
                            ) : null}
                          </TableCell>
                          <TableCell sx={{ whiteSpace: 'nowrap' }}>{fmtMoney(m.price, 2)}</TableCell>
                          <TableCell sx={{ whiteSpace: 'nowrap' }}>{fmtDay(m.expiryDate)}</TableCell>
                          <TableCell sx={{ fontFamily: 'monospace' }}>{m.serialNumber || '-'}</TableCell>
                          <TableCell sx={{ color: '#475569', maxWidth: 200 }}>{m.comment || '-'}</TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
            {moves.length >= data.limit && (
              <Typography sx={{ fontSize: '0.72rem', color: 'text.secondary', mt: 0.5 }}>Показаны первые {data.limit} изменений</Typography>
            )}
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default EntryHistoryDialog;
